import type { ProjectTheme } from './types'
import { normalizeProjectTheme } from './normalize'
import { applyColorsToTheme, applyFontsToTheme } from './presets'
import { COLOR_PALETTES, contrastRatio, distinctPrimaryColor, mostReadableOn, paletteColorValues } from './palettes'
import { FONT_PAIRS, activeFontPairId, applyFontPairToTheme, fontStack } from './fontPairs'
import { alignButtonText, buttonSizes } from './buttonTextSize'
import {
  BUTTON_LOOK_GROUPS,
  CARD_LOOK_GROUPS,
  SECTION_SPACING_LOOKS,
  applyButtonLookToTheme,
  applyCardLookToTheme,
  applySectionSpacingToTheme,
} from './styleLooks'
import { DESIGN_STYLES, applyDesignStyleToTheme, type DesignPalette, type DesignStyle } from './designStyles'

/**
 * Un estilo a medida: uno de los estilos de diseño y, encima, lo que se pida
 * cambiar de él —el color de marca, la pareja de letras, la forma de los
 * botones o de las tarjetas, el aire entre secciones—.
 *
 * «Brutalista pero verde» es `{ designStyleId: 'brutalist', color: '#16a34a' }`:
 * la estructura del brutalista —bordes, sombras duras, letra— con el verde en
 * los botones y el acento. La IA sólo devuelve ids y un color; todo lo demás lo
 * hace el código con las mismas funciones que el panel, así que el resultado
 * es siempre coherente y cada parte se puede cambiar después por separado.
 */
export type StyleRecipe = {
  designStyleId?: string
  /**
   * Una paleta entera: fondos, tarjetas, bordes, texto y acentos. Un id de
   * `COLOR_PALETTES`, o `style:<id>` para la paleta propia de un estilo —así
   * se puede cambiar de estilo sin que cambien los colores—.
   */
  paletteId?: string
  /**
   * Cuántos grados se gira el tono de TODA la paleta (-180…180).
   *
   * Es la forma de tener «la misma paleta, pero en verde» sin un color suelto
   * que no combine con los fondos: fondos, tarjetas, bordes y botones giran
   * juntos, conservando su luz y su saturación, así que siguen casando.
   */
  hue?: number
  /**
   * Los colores exactos que dio el cliente (`#FBF9F5`…), cada uno en su papel.
   * Van encima de la paleta elegida; lo que no dio se deriva de lo que sí, para
   * que el conjunto siga combinando y leyéndose (`paletteWithCustomColors`).
   */
  palette?: CustomPalette
  /** El color de marca, `#rrggbb`: va a los botones y al acento. */
  color?: string
  /** Un id de `FONT_PAIRS`, o `style:<id>` para la letra propia de un estilo. */
  fontPairId?: string
  /**
   * Una letra que no está en `FONT_PAIRS`: la de un template de sitio, que se
   * conserva al elegir un estilo. Sólo cuenta sin `fontPairId`.
   */
  fonts?: { heading: string; body: string; serif?: 'heading' | 'both' }
  /**
   * El peso y el espaciado de los títulos. Sin él, los del estilo base; con
   * él, cambiar de estilo no cambia cómo pesan los títulos (ver el asistente
   * de proyecto nuevo, donde el estilo va después de la letra).
   */
  headings?: DesignStyle['headings']
  button?: Partial<DesignStyle['button']>
  card?: Partial<DesignStyle['card']>
  section?: string
  /**
   * El fondo del pie, cuando el cliente lo pidió: un papel de la paleta
   * (`background`, `alternate`, `card`, `ink`, `button`) o un `#hex`. Sin él,
   * el del estilo (sobre la tinta). El texto del pie se ajusta solo al fondo.
   */
  footer?: string
}

const HEX = /^#[0-9a-f]{6}$/i

/** Los papeles que puede tomar el fondo del pie, y la variable del tema de cada uno. */
const FOOTER_ROLES: Record<string, string> = {
  background: 'background', alternate: 'backgroundSecondary', card: 'card', ink: 'text', button: 'primary',
}

/**
 * El pie con el fondo pedido. Un código que coincide con un color del tema va
 * como su variable —así sigue a la paleta si se cambia después—; uno que no,
 * tal cual.
 */
function withFooterBackground(theme: ProjectTheme, footer: string | undefined): ProjectTheme {
  if (!footer) return theme
  const colors = theme.colors as unknown as Record<string, string>
  const key = FOOTER_ROLES[footer]
    ?? Object.keys(colors).find((name) => typeof colors[name] === 'string' && colors[name].toUpperCase() === footer.toUpperCase())
  const value = key ? `var(--wb-${key})` : HEX.test(footer) ? footer.toUpperCase() : null
  if (!value) return theme
  return normalizeProjectTheme({
    ...theme,
    sectionComponents: theme.sectionComponents.map((component) => (
      component.id === 'section-footer'
        ? { ...component, styles: { ...component.styles, background: value, backgroundColor: value, backgroundImage: '' } }
        : component
    )),
  }, theme)
}

/** Los papeles de una paleta que el cliente puede fijar con su código. */
export const CUSTOM_PALETTE_ROLES = ['background', 'alternate', 'surface', 'card', 'border', 'ink', 'muted', 'button', 'accent'] as const
export type CustomPalette = Partial<Record<(typeof CUSTOM_PALETTE_ROLES)[number], string>>

function toRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function mix(a: string, b: string, amount: number): string {
  const ca = toRgb(a)
  const cb = toRgb(b)
  return '#' + ca.map((value, index) => Math.round(value + (cb[index]! - value) * amount).toString(16).padStart(2, '0')).join('').toUpperCase()
}

/**
 * El color de marca, ajustado hasta que se lea sobre el fondo: un amarillo
 * sobre blanco o un azul marino sobre negro se oscurecen o aclaran lo justo.
 */
export function brandColorOn(background: string, color: string, minContrast = 3): string {
  if (!HEX.test(color) || !HEX.test(background)) return color
  if (contrastRatio(background, color) >= minContrast) return color.toUpperCase()
  const towards = contrastRatio(background, '#000000') > contrastRatio(background, '#FFFFFF') ? '#000000' : '#FFFFFF'
  for (let step = 1; step <= 10; step += 1) {
    const next = mix(color, towards, step * 0.08)
    if (contrastRatio(background, next) >= minContrast) return next
  }
  return mix(color, towards, 0.8)
}

/** La paleta de un estilo con el color de marca en su sitio; los neutros no se tocan. */
export function paletteWithBrandColor(palette: DesignPalette, color: string): DesignPalette {
  let accent = brandColorOn(palette.background, color)
  let button = distinctPrimaryColor(accent, accent)
  /* Si el color elegido apenas llegaba al contraste mínimo, aclararlo lo puede
     hacer desaparecer contra el fondo. En ese caso se prepara primero una base
     algo más profunda; el primario derivado sigue siendo el más claro de los
     dos y conserva al menos 3:1 con la página. */
  if (contrastRatio(palette.background, button) < 3) {
    accent = brandColorOn(palette.background, color, 4)
    button = distinctPrimaryColor(accent, accent)
  }
  return { ...palette, button, accent }
}

/**
 * Una paleta con los colores exactos del cliente encima.
 *
 * Los que dio se respetan tal cual —son su marca—, salvo la tinta, que sube
 * hasta 4,5:1 sobre el fondo en su mismo tono (`brandColorOn`): un texto que no
 * se lee no es una decisión de marca. La letra encima del botón ya se elige
 * sola al aplicar la paleta. Lo que no dio se deriva:
 * con otro fondo, el alterno, la superficie, la tarjeta y el borde salen de
 * mezclar ese fondo con la tinta —el mismo matiz, no los grises de la paleta
 * base—; con un color de marca y sin acento, el acento es ese mismo color.
 *
 * Un «acento» que casi no se distingue del fondo no es un acento: es un fondo
 * («verde menta suave», «arena»). La IA lo ponía ahí como segundo color de
 * marca, y las estrellas, los iconos y los enlaces —que lo usan— desaparecían
 * sobre la página. Ése pasa a ser el fondo alterno de secciones, si no se dio
 * otro, y el acento toma el color de los botones. El umbral es bajo a propósito
 * (1,5:1): un amarillo de marca no llega a él y se respeta.
 */
export function paletteWithCustomColors(base: DesignPalette, custom: CustomPalette): DesignPalette {
  const given = (role: keyof CustomPalette) => {
    const value = custom[role]
    return value && HEX.test(value) ? value.toUpperCase() : undefined
  }
  const background = given('background') ?? base.background
  const newGround = Boolean(given('background'))
  const mutedOf = () => given('muted') ?? (newGround ? undefined : base.muted)
  const dark = isDarkColor(background)
  const fallbackInk = contrastRatio(background, base.ink) >= 4.5 ? base.ink : mostReadableOn(background, [base.ink, '#111111', '#FFFFFF'])
  const ink = brandColorOn(background, given('ink') ?? fallbackInk, 4.5)
  const derived = (role: 'alternate' | 'surface' | 'border', amount: number) => given(role) ?? (newGround ? mix(background, ink, amount) : base[role])
  const givenAccent = given('accent')
  const accentIsGround = Boolean(givenAccent) && contrastRatio(background, givenAccent!) < ACCENT_GROUND_CONTRAST
  /* Los de la paleta base sí se ajustan si el fondo cambió: ésos no los eligió
     nadie para este fondo. */
  /* Si el acento que dio era un fondo y dio un botón: un paso del botón hacia
     la tinta —el mismo color, más hondo—. Igual al botón no: los dos papeles
     van juntos y dejarían de leerse como piezas distintas. */
  const groundAccent = accentIsGround && given('button')
    ? brandColorOn(background, mix(given('button')!, ink, 0.22), 3)
    : undefined
  const accent = (accentIsGround ? groundAccent : givenAccent) ?? given('button') ?? (newGround ? brandColorOn(background, base.accent) : base.accent)
  const button = given('button') ?? given('accent') ?? (newGround ? brandColorOn(background, base.button) : base.button)
  return {
    background,
    alternate: given('alternate') ?? (accentIsGround ? givenAccent! : derived('alternate', dark ? 0.06 : 0.04)),
    surface: derived('surface', dark ? 0.1 : 0.07),
    card: given('card') ?? (newGround ? mix(background, '#FFFFFF', dark ? 0.06 : 0.6) : base.card),
    border: derived('border', dark ? 0.18 : 0.12),
    ink,
    /* El botón que dio el cliente, tal cual: es su marca. Sólo uno derivado se
       separa del acento (si no, botón y acento saldrían iguales). */
    button: given('button') ?? distinctPrimaryColor(button, accent),
    accent,
    /* El texto secundario sólo si se dio; con otro fondo, el de antes ya no
       vale y se vuelve a derivar. */
    ...(mutedOf() ? { muted: mutedOf()! } : {}),
  }
}

/** Por debajo de este contraste con el fondo, un «acento» es un color de fondo (ver `paletteWithCustomColors`). */
const ACCENT_GROUND_CONTRAST = 1.5

/** El prefijo de las paletas —y de la letra— que son las de un estilo de diseño. */
export const STYLE_PALETTE_PREFIX = 'style:'

/** Una pareja de letra, o la de un estilo (`style:<id>`): así se cambia de estilo sin que cambie la letra. */
function fontPairById(id: string | undefined): { heading: string; body: string; serif?: 'heading' | 'both' } | undefined {
  if (id?.startsWith(STYLE_PALETTE_PREFIX)) {
    return DESIGN_STYLES.find((style) => style.id === id.slice(STYLE_PALETTE_PREFIX.length))?.fonts
  }
  return id ? FONT_PAIRS.find((pair) => pair.id === id) : undefined
}

/** Una paleta del panel de Color —o la de un estilo— como paleta de estilo (sin su id ni sus etiquetas). */
export function paletteById(id: string | undefined): DesignPalette | undefined {
  if (id?.startsWith(STYLE_PALETTE_PREFIX)) {
    return DESIGN_STYLES.find((style) => style.id === id.slice(STYLE_PALETTE_PREFIX.length))?.palette
  }
  const found = id ? COLOR_PALETTES.find((palette) => palette.id === id) : undefined
  if (!found) return undefined
  const { id: _id, tags: _tags, ...palette } = found
  return palette
}

function toHsl(hex: string): [number, number, number] {
  const [r, g, b] = toRgb(hex).map((value) => value / 255) as [number, number, number]
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
  return [h * 60, s, l]
}

function fromHsl(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360 / 360
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const channel = (t: number) => {
    const x = t < 0 ? t + 1 : t > 1 ? t - 1 : t
    if (x < 1 / 6) return p + (q - p) * 6 * x
    if (x < 1 / 2) return q
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6
    return p
  }
  const values = s === 0 ? [l, l, l] : [channel(hue + 1 / 3), channel(hue), channel(hue - 1 / 3)]
  return '#' + values.map((value) => Math.round(value * 255).toString(16).padStart(2, '0')).join('').toUpperCase()
}

/** Un color con el tono girado; los grises se quedan grises. */
export function rotateHue(hex: string, degrees: number): string {
  if (!HEX.test(hex) || !degrees) return hex
  const [h, s, l] = toHsl(hex)
  return fromHsl(h + degrees, s, l)
}

/** Cuántos grados hay que girar `from` para llegar al tono de `to` (-180…180). */
export function hueDelta(from: string, to: string): number {
  if (!HEX.test(from) || !HEX.test(to)) return 0
  const delta = toHsl(to)[0] - toHsl(from)[0]
  return Math.round(((delta + 540) % 360) - 180)
}

/**
 * La paleta entera con el tono girado. El botón se vuelve a ajustar al fondo
 * al final: girar un amarillo a azul cambia cuánto se lee sobre blanco.
 */
export function paletteWithHue(palette: DesignPalette, degrees: number): DesignPalette {
  if (!degrees) return { ...palette, button: distinctPrimaryColor(palette.button, palette.accent) }
  const turned = Object.fromEntries(Object.entries(palette).map(([key, value]) => [key, rotateHue(value, degrees)])) as DesignPalette
  const accent = brandColorOn(turned.background, turned.accent)
  const button = brandColorOn(turned.background, turned.button)
  return { ...turned, button: distinctPrimaryColor(button, accent), accent }
}

function normalizeHue(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  const hue = Math.round(((value + 540) % 360) - 180)
  return hue === 0 ? undefined : hue
}

/** Sólo lo que existe: un id inventado o un color mal escrito se descartan. */
export function sanitizeStyleRecipe(raw: unknown): StyleRecipe {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const recipe: StyleRecipe = {}
  if (typeof source.designStyleId === 'string' && DESIGN_STYLES.some((style) => style.id === source.designStyleId)) {
    recipe.designStyleId = source.designStyleId
  }
  if (typeof source.paletteId === 'string' && paletteById(source.paletteId)) recipe.paletteId = source.paletteId
  const hue = normalizeHue(source.hue)
  if (hue !== undefined) recipe.hue = hue
  if (typeof source.color === 'string' && HEX.test(source.color.trim())) recipe.color = source.color.trim().toUpperCase()
  if (typeof source.fontPairId === 'string' && fontPairById(source.fontPairId)) recipe.fontPairId = source.fontPairId
  const fonts = (source.fonts && typeof source.fonts === 'object' ? source.fonts : null) as Record<string, unknown> | null
  const FAMILY = /^[\p{L}\p{N} .&'-]{1,60}$/u
  if (fonts && typeof fonts.heading === 'string' && FAMILY.test(fonts.heading) && typeof fonts.body === 'string' && FAMILY.test(fonts.body)) {
    recipe.fonts = { heading: fonts.heading, body: fonts.body, ...(fonts.serif === 'heading' || fonts.serif === 'both' ? { serif: fonts.serif } : {}) }
  }
  const customSource = (source.palette && typeof source.palette === 'object' ? source.palette : {}) as Record<string, unknown>
  const custom: CustomPalette = {}
  for (const role of CUSTOM_PALETTE_ROLES) {
    const value = customSource[role]
    if (typeof value === 'string' && HEX.test(value.trim())) custom[role] = value.trim().toUpperCase()
  }
  if (Object.keys(custom).length) recipe.palette = custom
  const headings = (source.headings && typeof source.headings === 'object' ? source.headings : null) as Record<string, unknown> | null
  if (headings && typeof headings.weight === 'string' && /^[1-9]00$/.test(headings.weight)
    && typeof headings.tracking === 'string' && /^-?\d*\.?\d+(em|px)$|^0$/.test(headings.tracking)) {
    recipe.headings = { weight: headings.weight, tracking: headings.tracking }
  }
  const pickLooks = (value: unknown, groups: { id: string; options: { id: string }[] }[]) => {
    const bag = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
    const picked: Record<string, string> = {}
    for (const group of groups) {
      const option = bag[group.id]
      if (typeof option === 'string' && group.options.some((item) => item.id === option)) picked[group.id] = option
    }
    return Object.keys(picked).length ? picked : undefined
  }
  const button = pickLooks(source.button, BUTTON_LOOK_GROUPS)
  if (button) recipe.button = button
  const card = pickLooks(source.card, CARD_LOOK_GROUPS)
  if (card) recipe.card = card
  if (typeof source.section === 'string' && SECTION_SPACING_LOOKS.some((look) => look.id === source.section)) recipe.section = source.section
  if (typeof source.footer === 'string') {
    const footer = source.footer.trim()
    if (FOOTER_ROLES[footer]) recipe.footer = footer
    else if (HEX.test(footer)) recipe.footer = footer.toUpperCase()
  }
  return recipe
}

export function styleRecipeIsEmpty(recipe: StyleRecipe): boolean {
  return Object.keys(recipe).length === 0
}

/** El estilo de diseño base con los cambios de la receta, como un estilo más. */
export function designStyleFromRecipe(recipe: StyleRecipe): DesignStyle | undefined {
  const base = DESIGN_STYLES.find((style) => style.id === recipe.designStyleId)
  if (!base) return undefined
  const pair = fontPairById(recipe.fontPairId) ?? recipe.fonts
  const chosen = paletteById(recipe.paletteId) ?? base.palette
  const palette = paletteWithHue(recipe.palette ? paletteWithCustomColors(chosen, recipe.palette) : chosen, recipe.hue ?? 0)
  return {
    ...base,
    palette: recipe.color ? paletteWithBrandColor(palette, recipe.color) : palette,
    fonts: pair ? { heading: pair.heading, body: pair.body, ...(pair.serif ? { serif: pair.serif } : {}), ...('script' in pair && pair.script ? { script: true } : {}) } : base.fonts,
    headings: recipe.headings ?? base.headings,
    button: { ...base.button, ...recipe.button },
    card: { ...base.card, ...recipe.card },
    section: recipe.section ?? base.section,
  }
}

/**
 * Aplica una receta. Con estilo base, se aplica el estilo ya modificado entero,
 * igual que al elegirlo en el panel. Sin él, cada parte pedida se aplica sobre
 * el tema tal como está: «pon los botones en píldora» no toca nada más.
 */
export function applyStyleRecipe(theme: ProjectTheme, recipe: StyleRecipe): ProjectTheme {
  const design = designStyleFromRecipe(recipe)
  if (design) return withFooterBackground(applyDesignStyleToTheme(theme, design), recipe.footer)

  const step = (next: Parameters<typeof normalizeProjectTheme>[0], previous: ProjectTheme) => normalizeProjectTheme(next, previous)
  let next = theme
  const current = next.colors
  const picked = paletteById(recipe.paletteId)
  const withCustom = recipe.palette
    ? paletteWithCustomColors(picked ?? {
      background: current.background,
      alternate: current.backgroundSecondary,
      surface: current.surface,
      card: current.card,
      border: current.border,
      ink: current.text,
      button: current.primary,
      accent: current.accent,
    }, recipe.palette)
    : picked
  const chosenPalette = withCustom && recipe.hue ? paletteWithHue(withCustom, recipe.hue) : withCustom
  if (chosenPalette) next = step(applyColorsToTheme(next, paletteColorValues({ ...chosenPalette, id: '', tags: [] })), next)
  if (recipe.color) {
    const colors = next.colors
    const palette = paletteWithBrandColor({
      background: colors.background,
      alternate: colors.backgroundSecondary,
      surface: colors.surface,
      card: colors.card,
      border: colors.border,
      ink: colors.text,
      button: colors.primary,
      accent: colors.accent,
    }, recipe.color)
    next = step(applyColorsToTheme(next, paletteColorValues({ ...palette, id: '', tags: [] })), next)
  }
  const known = fontPairById(recipe.fontPairId)
  if (known) next = step(applyFontPairToTheme(next, { id: recipe.fontPairId!, ...known }), next)
  else if (recipe.fonts) next = step(applyFontsToTheme(next, fontStack(recipe.fonts.heading, Boolean(recipe.fonts.serif)), fontStack(recipe.fonts.body, recipe.fonts.serif === 'both'), 'custom'), next)
  const pair = known ?? recipe.fonts
  for (const group of BUTTON_LOOK_GROUPS) {
    const look = group.options.find((option) => option.id === recipe.button?.[group.id as keyof DesignStyle['button']])
    if (look) next = step(applyButtonLookToTheme(next, look), next)
  }
  for (const group of CARD_LOOK_GROUPS) {
    const look = group.options.find((option) => option.id === recipe.card?.[group.id as keyof DesignStyle['card']])
    if (look) next = step(applyCardLookToTheme(next, look), next)
  }
  const spacing = SECTION_SPACING_LOOKS.find((look) => look.id === recipe.section)
  if (spacing) next = step(applySectionSpacingToTheme(next, spacing), next)
  if (pair) next = step(alignButtonText(next, buttonSizes(next)), next)
  return withFooterBackground(next, recipe.footer)
}

/**
 * Las opciones para el prompt, en pocas líneas: ids de letras, de looks y de
 * aire. Estáticas, así que van en la parte que se cachea.
 */
export function describeStyleOptionsForAi(): string {
  const looks = (prefix: string, groups: { id: string; options: { id: string }[] }[]) => groups
    .map((group) => `${prefix}.${group.id}: ${group.options.map((option) => option.id).join(', ')}`)
  return [
    'color: any brand color as #rrggbb (e.g. "green" → #16A34A). Goes on buttons and accents; backgrounds stay neutral.',
    `fontPairId: ${FONT_PAIRS.map((pair) => pair.id + (pair.serif ? ' (serif)' : '')).join(', ')}`,
    ...looks('button', BUTTON_LOOK_GROUPS),
    ...looks('card', CARD_LOOK_GROUPS),
    `section (spacing between sections): ${SECTION_SPACING_LOOKS.map((look) => look.id).join(', ')}`,
  ].join('\n')
}

const saturationOf = (hex: string) => (HEX.test(hex) ? toHsl(hex)[1] : 0)
const isDarkColor = (hex: string) => (HEX.test(hex) ? toHsl(hex)[2] < 0.5 : false)

/**
 * Un color suelto convertido en paleta.
 *
 * «Verde» como `color` pintaba de verde los botones sobre los fondos del
 * estilo, que a veces eran crema o azulados, y no casaban. Aquí el color se
 * resuelve con una paleta entera: se gira el tono de la que ya había hasta
 * llegar a él y, si esa no tiene color que girar —una de grises—, se elige la
 * de la casa más cercana con la misma luz. El resultado ya no lleva `color`.
 */
export function harmonizeRecipeColor(recipe: StyleRecipe): StyleRecipe {
  if (!recipe.color) return recipe
  const { color, ...rest } = recipe
  const own = rest.paletteId ?? (rest.designStyleId ? STYLE_PALETTE_PREFIX + rest.designStyleId : undefined)
  const base = paletteById(own)
  const dark = base ? isDarkColor(base.background) : false
  const withHue = (paletteId: string, from: string): StyleRecipe => {
    const hue = normalizeHue(hueDelta(from, color))
    const { hue: _previous, ...clean } = rest
    return { ...clean, paletteId, ...(hue !== undefined ? { hue } : {}) }
  }

  /* Negro, gris o blanco: no hay tono al que girar; una paleta sobria. */
  if (saturationOf(color) < 0.15) {
    const sober = COLOR_PALETTES.find((palette) => isDarkColor(palette.background) === dark && saturationOf(palette.button) < 0.15)
    return sober ? { ...rest, paletteId: sober.id } : rest
  }
  if (own && base && saturationOf(base.button) >= 0.25) return withHue(own, base.button)

  const candidates = COLOR_PALETTES.filter((palette) => isDarkColor(palette.background) === dark && saturationOf(palette.button) >= 0.25)
  const nearest = candidates.reduce<(typeof candidates)[number] | undefined>((best, palette) => (
    !best || Math.abs(hueDelta(palette.button, color)) < Math.abs(hueDelta(best.button, color)) ? palette : best
  ), undefined)
  return nearest ? withHue(nearest.id, nearest.button) : rest
}

/** Las paletas para el prompt: id, carácter, fondo y color de marca. */
/**
 * El nombre de un color en palabras: «cream», «deep teal», «black».
 *
 * Para la IA: un modelo de lenguaje no «ve» `#2485E7`, pero sí entiende «bright
 * blue». Con sólo los códigos elegía casi siempre las primeras paletas, las
 * neutras, aunque el negocio pidiera algo cálido.
 */
export function colorNameForAi(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return hex
  const n = Number.parseInt(m[1]!, 16)
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
  let h = 0
  if (d) h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4
  h = (h * 60 + 360) % 360
  // Un gris casi puro no tiene tono: su «matiz» es ruido.
  const warm = (h < 70 || h >= 330) && s >= 0.08
  if (s < 0.14 || (l > 0.93 && s < 0.5)) {
    if (l > 0.95) return warm && s > 0.2 ? 'cream' : 'white'
    if (l > 0.85) return warm ? 'warm off-white' : 'pale gray'
    if (l > 0.6) return 'light gray'
    if (l > 0.3) return 'gray'
    if (l > 0.12) return 'charcoal'
    return 'black'
  }
  const hue = h < 7 || h >= 345 ? 'red'
    : h < 30 ? (l < 0.45 ? 'rust' : 'coral')
    : h < 45 ? (l < 0.45 ? 'brown' : s < 0.6 && l > 0.5 ? 'gold' : 'orange')
    : h < 62 ? (l < 0.45 ? 'olive' : 'amber')
    : h < 75 ? 'yellow'
    : h < 100 ? 'lime'
    : h < 150 ? 'green'
    : h < 175 ? 'teal'
    : h < 200 ? 'cyan'
    : h < 235 ? 'blue'
    : h < 255 ? 'indigo'
    : h < 285 ? 'violet'
    : h < 315 ? 'purple'
    : 'pink'
  const tone = l > 0.85 ? 'pale ' : l > 0.7 ? 'light ' : l < 0.25 ? 'deep ' : l < 0.4 ? 'dark ' : s < 0.35 ? 'muted ' : s > 0.75 && l > 0.45 ? 'bright ' : ''
  return `${tone}${hue}`
}

/**
 * Las paletas para el prompt: id, etiquetas y sus colores en palabras (con el
 * código, para acercarse cuando el usuario da uno). `order` las baraja: con la
 * lista siempre en el mismo orden, la IA tiraba de las primeras.
 */
export function describePalettesForAi(order: <T>(items: T[]) => T[] = (items) => items): string {
  return order([...COLOR_PALETTES])
    .map((palette) => `${palette.id} (${palette.tags.join('/')}; ${colorNameForAi(palette.background)} background, ${colorNameForAi(palette.button)} brand ${palette.button})`)
    .join(', ')
}

/** La primera familia de una pila CSS: `'Lora', serif` → `Lora`. */
const familyOf = (stack: unknown) => (typeof stack === 'string' ? stack.split(',')[0]!.trim().replace(/^['"]|['"]$/g, '') : '')
const isSerif = (stack: unknown) => typeof stack === 'string' && /(^|,)\s*serif\s*$/.test(stack)

/**
 * La receta que describe un tema tal como está —sus colores, su letra y sus
 * títulos—, para empezar a personalizar un template de sitio sin perder lo
 * suyo: elegir después un estilo cambia la forma, no los colores ni la letra.
 */
export function recipeFromTheme(theme: ProjectTheme): StyleRecipe {
  const colors = theme.colors
  const palette: CustomPalette = {
    background: colors.background, alternate: colors.backgroundSecondary, surface: colors.surface, card: colors.card,
    border: colors.border, ink: colors.text, button: colors.primary, accent: colors.accent,
  }
  const pairId = activeFontPairId(theme)
  const heading = theme.roles.heading1
  const paragraph = theme.roles.paragraph
  const serif = isSerif(paragraph.fontFamily) ? 'both' as const : isSerif(heading.fontFamily) ? 'heading' as const : undefined
  return sanitizeStyleRecipe({
    palette,
    ...(pairId
      ? { fontPairId: pairId }
      : { fonts: { heading: familyOf(heading.fontFamily), body: familyOf(paragraph.fontFamily), ...(serif ? { serif } : {}) } }),
    headings: { weight: String(heading.fontWeight ?? ''), tracking: String(heading.letterSpacing ?? '') || '0' },
  })
}
